import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Subscription, interval } from 'rxjs';
import { AlertService } from './alert.service';
import { PermissionService } from './permissionservice.service';
import { isEnrollmentDuplicatesAlert } from '../utils/alert-event-type';
import { PermissionGroups } from '../utils/PermissionGroups';

const LAST_SEEN_KEY = 'lrnext.notifications.lastSeenAt';

/** Alert list / bell — EDI included; Member Services excluded (AssigneeOnly via API only). */
export const ALERT_CAPABLE_GROUPS = [
  PermissionGroups.Admin,
  PermissionGroups.BillingAdmin,
  PermissionGroups.BillingContributor,
  PermissionGroups.ClientServicesAdmin,
  PermissionGroups.ClientServicesContributor,
  PermissionGroups.EDIContributor
];

@Injectable({
  providedIn: 'root'
})
export class NotificationsInboxService implements OnDestroy {
  private readonly unreadCountSubject = new BehaviorSubject<number>(0);
  readonly unreadCount$ = this.unreadCountSubject.asObservable();

  private pollSub?: Subscription;
  private started = false;

  constructor(
    private alertService: AlertService,
    private permissionService: PermissionService
  ) {}

  canAccessAlerts(): boolean {
    return this.permissionService.hasPermission(ALERT_CAPABLE_GROUPS);
  }

  /** Department Admins + LRNext Admin + EDI see unscoped role alerts. */
  hasUnscopedAlertAccess(): boolean {
    return this.permissionService.hasPermission([
      PermissionGroups.Admin,
      PermissionGroups.BillingAdmin,
      PermissionGroups.ClientServicesAdmin,
      PermissionGroups.EDIContributor
    ]);
  }

  start(): void {
    if (this.started || !this.canAccessAlerts()) {
      return;
    }
    this.started = true;
    this.refreshUnreadCount();
    // Lightweight poll so the toolbar badge updates without opening the page.
    this.pollSub = interval(30_000).subscribe(() => this.refreshUnreadCount());
  }

  ngOnDestroy(): void {
    this.pollSub?.unsubscribe();
  }

  markSeen(): void {
    localStorage.setItem(LAST_SEEN_KEY, new Date().toISOString());
    this.unreadCountSubject.next(0);
  }

  refreshUnreadCount(): void {
    if (!this.canAccessAlerts()) {
      this.unreadCountSubject.next(0);
      return;
    }

    const lastSeen = this.getLastSeenAt();
    // Badge reflects personal (My alerts) unread count, not the admin "All" feed.
    this.alertService.list({ page: 1, pageSize: 50, all: false }).subscribe({
      next: (result) => {
        const items = result?.items ?? [];
        const unread = items.filter(a => {
          if (isEnrollmentDuplicatesAlert(a.eventType ?? a.category) || !a.createdAt) {
            return false;
          }
          const created = new Date(a.createdAt).getTime();
          return Number.isFinite(created) && created > lastSeen.getTime();
        }).length;
        this.unreadCountSubject.next(unread);
      },
      error: () => {
        // Keep prior count on transient failures (e.g. 403 for Member Services).
      }
    });
  }

  private getLastSeenAt(): Date {
    const raw = localStorage.getItem(LAST_SEEN_KEY);
    if (!raw) {
      // First visit: don't flood the badge with historical alerts.
      const now = new Date();
      localStorage.setItem(LAST_SEEN_KEY, now.toISOString());
      return now;
    }
    const parsed = new Date(raw);
    return Number.isFinite(parsed.getTime()) ? parsed : new Date(0);
  }
}
